import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    TextField,
    MenuItem,
    Button,
    Alert,
    Typography,
    Divider,
} from '@mui/material';
import { API_URL } from '../api';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const LEVEL_OPTIONS = ['', 'KS2', 'KS3', 'GCSE', 'A-Level'];
const YEAR_GROUP_OPTIONS = [
    '', 'Reception', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6',
    'Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11', 'Year 12', 'Year 13',
];

/**
 * Admin-side edit dialog for a Parent or Student account. Handles the
 * shape difference between the two roles:
 *   - Parent → `childInfo.{studentName,level,subject,yearGroup,school}` (nested)
 *   - Student → `level, subject, yearGroup, school` (top-level) + name/email
 *
 * The admin PATCH endpoint (`updateAccount`) already accepts every field
 * we send; this component just gathers them and posts.
 */
const EditAccountDetailsDialog = ({ open, account, onClose, onSaved }) => {
    const isParent = account?.role === 'parent';
    const [subjects, setSubjects] = useState([]);
    const [form, setForm] = useState(() => defaults(account));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => { setForm(defaults(account)); setError(''); }, [account]);

    useEffect(() => {
        if (!open) return;
        axios.get(`${API_URL}/api/subjects?activeOnly=true`)
            .then((res) => setSubjects(res.data || []))
            .catch(() => setSubjects([]));
    }, [open]);

    if (!account) return null;

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async () => {
        setBusy(true); setError('');
        try {
            const payload = {
                firstName: form.firstName,
                surname: form.surname,
                phone: form.phone,
                homeAddress: {
                    line1: form.addressLine1,
                    line2: form.addressLine2,
                    city: form.city,
                    postcode: form.postcode,
                },
            };
            if (isParent) {
                payload.childInfo = {
                    studentName: form.studentName,
                    level: form.level || undefined,
                    subject: form.subject,
                    yearGroup: form.yearGroup,
                    school: form.school,
                    // CR-B1: parent-managed child DOB lives on childInfo.
                    // Blank string clears it back to null on the server.
                    dateOfBirth: form.dateOfBirth || null,
                };
            } else {
                payload.level = form.level || undefined;
                payload.subject = form.subject;
                payload.yearGroup = form.yearGroup;
                payload.school = form.school;
                // CR-B1: top-level DOB for standalone student accounts.
                payload.dateOfBirth = form.dateOfBirth || null;
            }
            const { data } = await axios.patch(
                `${API_URL}/api/admin/accounts/${account._id}`,
                payload,
                authHeaders()
            );
            onSaved?.(data);
            onClose?.();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to save.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Edit account details</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
                    <Typography variant="caption" color="text.secondary">
                        Admin can override any field the account holder can't self-edit. Email cannot
                        be changed here — use the Account status card + a fresh invite if the address
                        needs to change.
                    </Typography>

                    <Typography variant="subtitle2">Contact</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField size="small" label="First name" fullWidth value={form.firstName} onChange={update('firstName')} />
                        <TextField size="small" label="Surname" fullWidth value={form.surname} onChange={update('surname')} />
                    </Stack>
                    <TextField size="small" label="Email" fullWidth value={account.email} disabled />
                    <TextField size="small" label="Phone" fullWidth value={form.phone} onChange={update('phone')} />

                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Home address</Typography>
                    <TextField size="small" label="Address line 1" fullWidth value={form.addressLine1} onChange={update('addressLine1')} />
                    <TextField size="small" label="Address line 2" fullWidth value={form.addressLine2} onChange={update('addressLine2')} />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField size="small" label="City" fullWidth value={form.city} onChange={update('city')} />
                        <TextField size="small" label="Postcode" fullWidth value={form.postcode} onChange={update('postcode')} />
                    </Stack>

                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">
                        {isParent ? 'Child details' : 'Academic profile'}
                    </Typography>

                    {isParent && (
                        <TextField size="small" label="Student name" fullWidth value={form.studentName} onChange={update('studentName')} />
                    )}
                    {/* CR-B1: date of birth. Feeds into the daily birthday
                        cron (student, tutor, admin, and any linked parent). */}
                    <TextField
                        size="small"
                        type="date"
                        label={isParent ? "Child's date of birth" : 'Date of birth'}
                        fullWidth
                        value={form.dateOfBirth}
                        onChange={update('dateOfBirth')}
                        InputLabelProps={{ shrink: true }}
                        helperText="Used only for birthday emails — leave blank to skip."
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            select size="small" label="Level" fullWidth
                            value={form.level} onChange={update('level')}
                        >
                            {LEVEL_OPTIONS.map((l) => (
                                <MenuItem key={l || 'any'} value={l}>{l || '—'}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select size="small" label="Subject" fullWidth
                            value={form.subject} onChange={update('subject')}
                        >
                            <MenuItem value="">—</MenuItem>
                            {subjects.map((s) => (
                                <MenuItem key={s._id || s.name} value={s.name}>{s.name}</MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            select size="small" label="Year group" fullWidth
                            value={form.yearGroup} onChange={update('yearGroup')}
                        >
                            {YEAR_GROUP_OPTIONS.map((y) => (
                                <MenuItem key={y || 'any'} value={y}>{y || '—'}</MenuItem>
                            ))}
                        </TextField>
                        <TextField size="small" label="School" fullWidth value={form.school} onChange={update('school')} />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy}>
                    {busy ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// HTML `type="date"` expects yyyy-mm-dd strings. Coerce a Date / ISO to that.
const toDateInput = (d) => {
    if (!d) return '';
    try {
        const dt = new Date(d);
        if (Number.isNaN(dt.getTime())) return '';
        return dt.toISOString().slice(0, 10);
    } catch {
        return '';
    }
};

function defaults(account) {
    if (!account) {
        return {
            firstName: '', surname: '', phone: '',
            addressLine1: '', addressLine2: '', city: '', postcode: '',
            studentName: '', level: '', subject: '', yearGroup: '', school: '',
            dateOfBirth: '',
        };
    }
    const isParent = account.role === 'parent';
    return {
        firstName: account.firstName || '',
        surname: account.surname || '',
        phone: account.phone || '',
        addressLine1: account.homeAddress?.line1 || '',
        addressLine2: account.homeAddress?.line2 || '',
        city: account.homeAddress?.city || '',
        postcode: account.homeAddress?.postcode || '',
        studentName: isParent ? (account.childInfo?.studentName || '') : '',
        level: isParent ? (account.childInfo?.level || '') : (account.level || ''),
        subject: isParent ? (account.childInfo?.subject || '') : (account.subject || ''),
        yearGroup: isParent ? (account.childInfo?.yearGroup || '') : (account.yearGroup || ''),
        school: isParent ? (account.childInfo?.school || '') : (account.school || ''),
        dateOfBirth: isParent
            ? toDateInput(account.childInfo?.dateOfBirth)
            : toDateInput(account.dateOfBirth),
    };
}

export default EditAccountDetailsDialog;
